import { z } from "zod";

import { PaymentFeeKind, type PaymentFee } from "../../../types/paymentFees.types";
import { priceShape, refinePrice, seedPrice, type FeeFormMessages } from "../feeFormSchema";

export type FeeRuleStep = "kind" | "where" | "price" | "review";

export function buildFeeRuleSchema(messages: Omit<FeeFormMessages, "corridorRequired">) {
  return z
    .object({
      feeKind: z.enum([PaymentFeeKind.COLOCA_COMMISSION, PaymentFeeKind.PROVIDER_COST]),
      country: z.string(),
      rail: z.string(),
      direction: z.string(),
      pinCostRoute: z.boolean(),
      ...priceShape(messages),
      effectiveNow: z.boolean(),
      validFromDate: z.string(),
      validFromTime: z.string(),
      validUntilDate: z.string(),
      validUntilTime: z.string(),
      reason: z.string().max(500),
      requiresAck: z.boolean(),
      acknowledged: z.boolean(),
    })
    .superRefine((values, ctx) => {
      if (!values.effectiveNow && !values.validFromDate) {
        ctx.addIssue({ code: "custom", path: ["validFromDate"], message: messages.validFromRequired });
      }
      if (values.validUntilDate) {
        const until = Date.parse(`${values.validUntilDate}T${values.validUntilTime || "00:00"}`);
        const from = values.effectiveNow
          ? Date.now()
          : Date.parse(`${values.validFromDate}T${values.validFromTime || "00:00"}`);
        if (!Number.isNaN(until) && !Number.isNaN(from) && until <= from) {
          ctx.addIssue({ code: "custom", path: ["validUntilDate"], message: messages.validUntil });
        }
      }
      if (values.requiresAck) {
        if (!values.reason.trim()) {
          ctx.addIssue({ code: "custom", path: ["reason"], message: messages.reasonRequired });
        }
        if (!values.acknowledged) {
          ctx.addIssue({ code: "custom", path: ["acknowledged"], message: messages.ackRequired });
        }
      }
      refinePrice(values, ctx, messages);
    });
}

export type FeeRuleFormValues = z.input<ReturnType<typeof buildFeeRuleSchema>>;

export const PRICE_STEP_FIELDS = [
  "percentage",
  "fixed",
  "minFee",
  "maxFee",
  "tiers",
  "validFromDate",
  "validUntilDate",
] as const satisfies readonly (keyof FeeRuleFormValues)[];

export function seedFeeRuleForm(feeKind: PaymentFeeKind, source: PaymentFee | null): FeeRuleFormValues {
  return {
    feeKind: source?.fee_kind ?? feeKind,
    country: source?.country ?? "",
    rail: source?.rail ?? "",
    direction: source?.direction ?? "",
    pinCostRoute: source ? source.provider_code != null : true,
    ...seedPrice(source),
    effectiveNow: true,
    validFromDate: "",
    validFromTime: "09:00",
    validUntilDate: "",
    validUntilTime: "",
    reason: "",
    requiresAck: false,
    acknowledged: false,
  };
}
